import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import FormattedCurrency from 'components/ui/FormattedCurrency';
import SectionHeaderBar from 'components/ui/SectionHeaderBar';
import { TableHeader, TableRow } from 'components/ui/StyledTable';
import { brandCode } from 'constants/brandCodes';
import { legsShape } from 'constants/propTypes';
import { isDBLeg } from 'utils/segmentTypes';
import VatAddress from './VatAddress';
import CountriesVAT from './CountriesVAT';
import DBVatInstructions from './DBVatInstructions';

import { VatBreakdownContainer, StyledTable, StyledCell, StyledPassengerName } from './VatBreakdown.styles';

const PARSE_DATE_FORMAT = 'YYYY-MM-DD';
const DATE_FORMAT = 'ddd Do MMMM YYYY';

const VatJourneyHeading = ({ direction, origin, destination, departureDate }) => (
  <h4>
    <FormattedMessage id={`journeySummary.${direction}`} /> -{' '}
    {`${moment(departureDate, PARSE_DATE_FORMAT).format(DATE_FORMAT)} - ${origin}`} <FormattedMessage id="bookingMagnet.to" /> {destination}
  </h4>
);

const VatTableHeader = ({ ticketNumber }) => (
  <TableHeader columns={12} data-cy="vat-breakdown-table-header">
    <StyledCell span={4}>
      <FormattedMessage id="journeySegment.ticketNumber" /> {ticketNumber}
    </StyledCell>
    <StyledCell span={2}>
      <FormattedMessage id="receipt.excludingVat" />
    </StyledCell>
    <StyledCell span={2}>
      <FormattedMessage id="receipt.vatRate" />
    </StyledCell>
    <StyledCell span={2}>
      <FormattedMessage id="receipt.vat" />
    </StyledCell>
    <StyledCell span={2}>
      <FormattedMessage id="receipt.includingVat" />
    </StyledCell>
  </TableHeader>
);

const VatBreakdownTable = ({ direction, ticketNumber, origin, destination, departureDate, vat, serviceTypeCode }) => (
  <React.Fragment>
    <VatJourneyHeading direction={direction} origin={origin} destination={destination} departureDate={departureDate} />
    <StyledTable data-cy="vat-breakdown-table">
      <VatTableHeader ticketNumber={ticketNumber} />
      {vat.map(({ countryCode, rate, amount, price: { currency, excludingVat, includingVat } = {} }) => (
        <TableRow data-cy="vat-breakdown-table-row" columns={12} key={countryCode}>
          <StyledCell span={4}>
            <CountriesVAT countryCode={countryCode} serviceTypeCode={serviceTypeCode} />
          </StyledCell>
          <StyledCell span={2}>{excludingVat ? <FormattedCurrency amount={excludingVat} currency={currency} /> : null}</StyledCell>
          <StyledCell span={2}>{(rate * 100).toFixed(2)}%</StyledCell>
          <StyledCell span={2}>
            <FormattedCurrency amount={amount} currency={currency} />
          </StyledCell>
          <StyledCell span={2}>{includingVat ? <FormattedCurrency amount={includingVat} currency={currency} /> : null}</StyledCell>
        </TableRow>
      ))}
    </StyledTable>
  </React.Fragment>
);

const DBVatBreakdownTable = ({ direction, ticketNumber, origin, destination, departureDate, price, currency }) => (
  <React.Fragment>
    <VatJourneyHeading direction={direction} origin={origin} destination={destination} departureDate={departureDate} />
    <StyledTable data-cy="vat-breakdown-table">
      <VatTableHeader ticketNumber={ticketNumber} />
      <TableRow data-cy="vat-breakdown-table-row" columns={12}>
        <StyledCell span={4}>{brandCode.DB}</StyledCell>
        <StyledCell span={6} />
        <StyledCell span={2}>
          <FormattedCurrency amount={price} currency={currency} />
        </StyledCell>
      </TableRow>
    </StyledTable>
  </React.Fragment>
);

const LegVatBreakdown = ({ isDB, vat, serviceTypeCode, price, currency, ...journey }) =>
  isDB ? (
    <DBVatBreakdownTable {...journey} price={price} currency={currency} />
  ) : (
    <VatBreakdownTable {...journey} vat={vat} serviceTypeCode={serviceTypeCode} />
  );

const VatBreakdown = ({ outbound = [], inbound = [] }) => {
  const vatInfo = useMemo(() => {
    const vatObject = {
      passengers: {},
      countries: [],
      hasDBLegs: false
    };

    [...outbound, ...inbound].forEach((leg) => {
      const { origin, destination, timing, direction, products, serviceType } = leg;
      const isDB = isDBLeg(leg);

      products.forEach(
        ({ canShowVAT, cancelled, vat = [], passenger: { id, firstName, lastName } = {}, ticketNumber, price, currency }) => {
          if (isDB && (leg.cancelled || cancelled)) {
            return;
          }

          const filteredVats =
            !isDB && canShowVAT
              ? vat.filter(({ amount, countryCode }) => {
                  if (amount > 0) {
                    vatObject.countries.push({ countryCode, serviceTypeCode: serviceType?.code });
                    return true;
                  }

                  return false;
                })
              : [];

          if (!isDB && filteredVats.length === 0) {
            return;
          }

          if (isDB) {
            vatObject.hasDBLegs = true;
          }

          const vatData = {
            ticketNumber,
            origin: origin.name,
            destination: destination.name,
            departureDate: timing.date,
            serviceType,
            vat: filteredVats,
            isDB,
            price,
            currency
          };

          if (!vatObject?.passengers?.[id]) {
            vatObject.passengers[id] = {
              passengerFirstName: firstName,
              passengerLastName: lastName,
              [direction]: [vatData]
            };
          } else if (!vatObject?.passengers?.[id]?.[direction]) {
            vatObject.passengers[id][direction] = [vatData];
          } else {
            vatObject.passengers[id][direction].push(vatData);
          }
        }
      );
    });

    return vatObject;
  }, [outbound, inbound]);

  if (Object.keys(vatInfo.passengers).length === 0) {
    return null;
  }

  return (
    <>
      <SectionHeaderBar>
        <FormattedMessage id="receipt.paymentsVat" />
      </SectionHeaderBar>
      <VatBreakdownContainer data-cy="vat-breakdown">
        {Object.entries(vatInfo.passengers).map(([id, { passengerFirstName, passengerLastName, outbound, inbound }]) => (
          <React.Fragment key={id}>
            <StyledPassengerName>
              {passengerFirstName} {passengerLastName}
            </StyledPassengerName>
            {outbound && outbound.length > 0
              ? outbound.map(({ ticketNumber, origin, destination, departureDate, vat, serviceType, isDB, price, currency }) => (
                  <LegVatBreakdown
                    key={`${ticketNumber}-${origin}-${departureDate}`}
                    direction="outbound"
                    ticketNumber={ticketNumber}
                    origin={origin}
                    destination={destination}
                    departureDate={departureDate}
                    serviceTypeCode={serviceType?.code}
                    vat={vat}
                    isDB={isDB}
                    price={price}
                    currency={currency}
                  />
                ))
              : null}
            {inbound && inbound.length > 0
              ? inbound.map(({ ticketNumber, origin, destination, departureDate, vat, serviceType, isDB, price, currency }) => (
                  <LegVatBreakdown
                    key={`${ticketNumber}-${origin}-${departureDate}`}
                    direction="inbound"
                    ticketNumber={ticketNumber}
                    origin={origin}
                    destination={destination}
                    serviceTypeCode={serviceType?.code}
                    departureDate={departureDate}
                    vat={vat}
                    isDB={isDB}
                    price={price}
                    currency={currency}
                  />
                ))
              : null}
          </React.Fragment>
        ))}
      </VatBreakdownContainer>
      {vatInfo.hasDBLegs ? <DBVatInstructions /> : null}
      <VatAddress vatInfo={vatInfo.countries} dataCy="vat-addresses" />
    </>
  );
};

VatBreakdown.propTypes = {
  outbound: legsShape,
  inbound: legsShape
};

VatJourneyHeading.propTypes = {
  direction: PropTypes.string,
  origin: PropTypes.string,
  destination: PropTypes.string,
  departureDate: PropTypes.string
};

VatTableHeader.propTypes = {
  ticketNumber: PropTypes.string
};

VatBreakdownTable.propTypes = {
  ...VatJourneyHeading.propTypes,
  ticketNumber: PropTypes.string,
  serviceTypeCode: PropTypes.string,
  vat: PropTypes.arrayOf(
    PropTypes.shape({
      countryCode: PropTypes.string,
      rate: PropTypes.number,
      amount: PropTypes.number,
      price: PropTypes.shape({
        currency: PropTypes.string,
        excludingVat: PropTypes.number,
        includingVat: PropTypes.number
      })
    })
  )
};

DBVatBreakdownTable.propTypes = {
  ...VatJourneyHeading.propTypes,
  ticketNumber: PropTypes.string,
  price: PropTypes.number,
  currency: PropTypes.string
};

LegVatBreakdown.propTypes = {
  ...VatBreakdownTable.propTypes,
  ...DBVatBreakdownTable.propTypes,
  isDB: PropTypes.bool
};

export default VatBreakdown;
